// libs
import React, { memo, useEffect, useState, useRef } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

// components
import PincodeInput from 'shared/layouts/DetailsPage/components/ProductInfo/components/PinCodeInput';

//styles
import { PincodeContainer, DeliveryText, PincodeInputContainer, TrailingButton } from './styles';

// helpers
import { setPincodeToStorage } from 'shared/layouts/DetailsPage/components/ProductInfo/storage';
import {
  getPinCodeDataFromStorage,
  getPinCodeLabel,
  setPinCodeToStorage as setPincodeDataToStorage,
} from './helpers';

// hooks
import { useOutsideAlerter } from 'shared/layouts/DetailsPage/components/ProductInfo/Desktop/components/EcomPolicy/hooks';

import useEDD from 'shared/components/EstimatedDeliveryDetails/hooks/useEdd';

// constants
import {
  PINCODE_PLACEHOLDER,
  BUTTON_STATE,
  DELIVERY_TEXT,
  PINCODE_ERROR_TEXT,
  INVALID_PINCODE_TEXT,
} from './constants';
import { ProductTypes } from 'shared/helpers/productHelpers';

// analytics
import { onPincodeApply } from './analytics';

// types
import { PincodeApiResponse } from 'shared/layouts/DetailsPage/components/ProductInfo/types';

function PinCodeServiceExp({
  setIsServiceable,
  isMobile = false,
  inStockSKUs,
  setFetchEstimatedDeliveryDetails,
  serviceabiltyResponse,
  setError,
  error,
  isConfigurableProduct,
  setIsFetchingEdd = () => {},
}: {
  isMobile?: boolean;
  setIsServiceable: (flag: boolean) => void;
  inStockSKUs: string[];
  setFetchEstimatedDeliveryDetails: () => void;
  serviceabiltyResponse: PincodeApiResponse;
  setError: () => void;
  error: string;
  isConfigurableProduct: boolean;
  setIsFetchingEdd?: (isFetching: boolean) => void;
}) {
  const inputRef = useRef(null);
  const formRef = useRef(null);
  // const inStockSKUs = useSelector(getAllInStockSKUs);
  const [pincode, setPincode] = useState('');
  const [previousPincode, setPreviousPincode] = useState('');
  const [helperText, setHelperText] = useState('');
  const [autofocusState, setAutofocusState] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const { fetchData } = useEDD();

  const [apiResponse, setApiResponse] = useState<PincodeApiResponse>(getPinCodeDataFromStorage());

  useEffect(() => {
    // set apiResponse which is received from parent on component mount
    if (isEmpty(serviceabiltyResponse) === false) {
      setApiResponse({
        ...serviceabiltyResponse,
      });
      setPincodeDataToStorage({
        response: serviceabiltyResponse,
        pincode: serviceabiltyResponse?.pincode || '',
        status: 'success',
      });
    }
  }, [serviceabiltyResponse]);

  useEffect(() => {
    if (pincode.length < 6) {
      setHelperText('');
      setError(null);
    }
    if (pincode !== '' && pincode === previousPincode) {
      const deliveryText = getPinCodeLabel(apiResponse);
      setHelperText(deliveryText);
      setError(null);
    }
  }, [apiResponse, pincode, previousPincode, setError]);

  useEffect(() => {
    if (isEmpty(apiResponse) === false) {
      const deliveryText = getPinCodeLabel(apiResponse);
      setHelperText(deliveryText);
      setIsServiceable(true);
      setPreviousPincode(apiResponse.pincode);
      setPincode(apiResponse.pincode);
    }
  }, [apiResponse, setIsServiceable]);

  // setting the trailing button value to apply on focus
  useEffect(() => {
    if (inputRef.current === document.activeElement) {
      setShowApply(true);
    }
  }, [setShowApply, inputRef]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length < 6) {
      setIsServiceable(true);
    }
    // Pincode can't have a length greater than 6
    if (e.target.value.length >= 7) {
      return;
    }
    const pincodeValue = e.target.value;
    setPincode(pincodeValue);
  };

  const pincodeResponseSuccessHandler = (pincodeDetails) => {
    const pincodeService = pincodeDetails?.pincodeDetails;
    const pincodeFetchData = pincodeDetails?.skuDetails;
    if (pincodeService?.serviceable === true) {
      onPincodeApply({
        deliveryDetails: pincodeFetchData?.details,
        previousPincode,
        pincodeOnPdp: pincode,
      });

      setError(null);
      setApiResponse({
        ...pincodeService,
        pincode,
      });
      setFetchEstimatedDeliveryDetails(pincodeFetchData);
      setPreviousPincode(pincode);
      const deliveryText = getPinCodeLabel({ ...pincodeService, pincode });
      setHelperText(deliveryText);
      setPincodeDataToStorage({ response: pincodeService, pincode, status: 'success' });
      setPincodeToStorage(pincode);
      setIsServiceable(true);
      return;
    }

    if (pincodeService.serviceable === false) {
      const message = get(pincodeDetails, 'message', PINCODE_ERROR_TEXT);
      setHelperText(message);
      setIsServiceable(false);
      onPincodeApply({
        previousPincode,
        pincodeOnPdp: pincode,
        pdpCheckDeliveryErrorMessage: message,
      });
      setError(PINCODE_ERROR_TEXT);
    }
  };

  const handleClick = async () => {
    if (pincode < 100000) {
      setError(PINCODE_ERROR_TEXT);
      setHelperText(INVALID_PINCODE_TEXT);
      return;
    }
    try {
      setIsFetchingEdd(true);
      fetchData({
        pinCode: Number(pincode),
        SKUs: inStockSKUs,
        successCallback: (pincodeRes) => {
          setIsFetchingEdd(false);
          // updateFetchEstimatedDeliveryDetails && updateFetchEstimatedDeliveryDetails(pincodeRes);
          // pinCodeChangeSuccessHandler(pincodeRes?.data?.pincodeDetails);
          pincodeResponseSuccessHandler(pincodeRes?.data);
        },
        failureCallback: (errorMessage: string) => {
          setIsFetchingEdd(false);
          setError(PINCODE_ERROR_TEXT);
          setHelperText(errorMessage || PINCODE_ERROR_TEXT);
          setIsServiceable(false);
        },
        type: isConfigurableProduct ? ProductTypes.CONFIGURABLE : ProductTypes.SIMPLE,
      });
    } catch (err) {
      // err
    }
  };

  // Note: this is added for Firefox.
  // Firefox allows users to type in characters for input["number"]
  const handleKeyPress = (e: React.ChangeEvent<HTMLInputElement>) => {
    const regex = new RegExp(/^[0-9]*$/);
    return e.key && !e.key.match(regex) && e.preventDefault();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();

    const regex = new RegExp(/^[0-9]*$/);

    // the value that user is trying to paste
    const pasteData = e.clipboardData.getData('text');

    // if pasteData is of type mumber then only allow pasting the text
    if (pasteData && pasteData.match(regex)) {
      setPincode(pasteData.substring(0, 6));
    }
  };

  // on press enter
  const handleFormSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (pincode.length < 6) {
      return;
    }
    handleClick();
    setAutofocusState(false);
    inputRef.current && inputRef.current.blur();
    setShowApply(false);
  };

  const onClear = () => {
    setPincode('');
    setIsServiceable(true);
    setAutofocusState(true);
    inputRef.current && inputRef.current.focus();
  };

  const onEdit = () => {
    setAutofocusState(true);
    inputRef.current && inputRef.current.focus();
    if (previousPincode === pincode) return;
    setIsServiceable(true);
    setShowApply(true);
  };

  const onClickOutsidePinCode = (isError) => {
    setShowApply(false);
    if (!isError) {
      setIsServiceable(true);
    }
  };

  const isError = error !== null;

  useOutsideAlerter(formRef, () => onClickOutsidePinCode(isError));

  const BUTTON_TEXT =
    pincode.length < 6 || (previousPincode !== pincode && !isError) || showApply
      ? BUTTON_STATE.APPLY
      : isError
      ? BUTTON_STATE.CLEAR
      : BUTTON_STATE.EDIT;

  const trailingButtonClick =
    pincode.length < 6 || (previousPincode !== pincode && !isError) || showApply
      ? handleClick
      : isError
      ? onClear
      : onEdit;

  const hideDeliveryText = pincode !== '' && pincode === previousPincode;

  return (
    <PincodeContainer>
      <PincodeInputContainer>
        {!isMobile && !hideDeliveryText && <DeliveryText>{DELIVERY_TEXT}</DeliveryText>}
        {isMobile && <DeliveryText>{DELIVERY_TEXT}</DeliveryText>}
        <form onSubmit={handleFormSubmit} ref={formRef}>
          <PincodeInput
            value={pincode}
            onChange={(e) => handleChange(e)}
            onKeyPress={(e) => handleKeyPress(e)}
            placeholder={PINCODE_PLACEHOLDER}
            type="number"
            isError={isError}
            helperText={helperText}
            label="Enter Pincode"
            autoFocus={autofocusState}
            onFocus={() => {
              setAutofocusState(true);
            }}
            onPaste={onPaste}
            ref={inputRef}
            trailingComponent={
              <TrailingButton
                type="button"
                tabIndex={0}
                onClick={trailingButtonClick}
                aria-label="close"
                disabled={pincode.length !== 6}
              >
                {BUTTON_TEXT}
              </TrailingButton>
            }
          />
        </form>
      </PincodeInputContainer>
    </PincodeContainer>
  );
}

export default memo(PinCodeServiceExp);
